import { useState, useEffect, useCallback } from "react";
import { MeasurementService } from "../services/measurementService";
import { MeasurementLogFilters } from "../types/measurement";
import {
  DatedMeasurementGroup,
  groupMeasurementsByDate,
} from "../utils/measurementGrouping";

const DATES_PER_PAGE = 10;

interface HistoryPage {
  groups: DatedMeasurementGroup[];
  // The date group right after this page's last one, fetched separately so the
  // last group's change badges still resolve across the page boundary.
  previousForLast: DatedMeasurementGroup | undefined;
  totalDates: number;
  totalPages: number;
  totalMeasurements: number;
}

const EMPTY_PAGE: HistoryPage = {
  groups: [],
  previousForLast: undefined,
  totalDates: 0,
  totalPages: 0,
  totalMeasurements: 0,
};

const countRows = (groups: DatedMeasurementGroup[]): number =>
  groups.reduce(
    (sum, group) =>
      sum +
      Object.values(group.byType).reduce(
        (n, rows) => n + (rows?.length ?? 0),
        0
      ),
    0
  );

/**
 * A user's measurement history grouped by date, mirroring the mobile history
 * screen. Server-paginated by DATE via /grouped (placeholders excluded there),
 * with one extra single-date request per page so the last group's
 * change-vs-previous badge can see across the page boundary.
 *
 * A measured_at filter switches to the flat /filter endpoint for that single
 * day - /grouped has no date param - and shows no change badges, since the
 * previous date is unknown in that view.
 *
 * No-ops when userId is null (invalid route param).
 */
export const useUserMeasurementHistory = (userId: number | null) => {
  const [data, setData] = useState<HistoryPage>(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<MeasurementLogFilters>({});

  const fetchPage = useCallback(async () => {
    if (!userId) {
      setData(EMPTY_PAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (filters.measured_at) {
        const result = await MeasurementService.getUserMeasurementLogs(
          userId,
          1,
          filters,
          100
        );
        if (!result) {
          setError("Failed to fetch measurement logs");
          setData(EMPTY_PAGE);
          return;
        }

        // groupMeasurementsByDate also drops the value=0 placeholders the
        // flat endpoint still returns
        const groups = groupMeasurementsByDate(result.items);
        setData({
          groups,
          previousForLast: undefined,
          totalDates: groups.length,
          totalPages: groups.length > 0 ? 1 : 0,
          totalMeasurements: countRows(groups),
        });
        return;
      }

      const main = await MeasurementService.getUserMeasurementsGroupedByDate(
        userId,
        page,
        DATES_PER_PAGE,
        filters.measurement_type
      );
      if (!main) {
        setError("Failed to fetch measurement logs");
        setData(EMPTY_PAGE);
        return;
      }

      const groups = groupMeasurementsByDate(main.rows);

      // Dates are the pagination unit, so the (page * per_page + 1)-th date is
      // exactly one request with per_page=1. Best-effort: on failure the last
      // group just shows no change badges.
      let previousForLast: DatedMeasurementGroup | undefined;
      if (page * DATES_PER_PAGE < main.meta.total_dates) {
        const peek = await MeasurementService.getUserMeasurementsGroupedByDate(
          userId,
          page * DATES_PER_PAGE + 1,
          1,
          filters.measurement_type
        );
        if (peek) previousForLast = groupMeasurementsByDate(peek.rows)[0];
      }

      setData({
        groups,
        previousForLast,
        totalDates: main.meta.total_dates,
        totalPages: main.meta.total_pages,
        totalMeasurements: main.meta.total_measurements,
      });
    } catch (err) {
      setError("An unexpected error occurred while fetching measurement logs");
      setData(EMPTY_PAGE);
    } finally {
      setLoading(false);
    }
  }, [userId, page, filters]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Previous group for the change badges: the next one within the page, or the
  // peeked cross-boundary group for the page's last date.
  const previousOf = useCallback(
    (group: DatedMeasurementGroup): DatedMeasurementGroup | undefined => {
      const index = data.groups.indexOf(group);
      if (index < 0) return undefined;
      return data.groups[index + 1] ?? data.previousForLast;
    },
    [data]
  );

  // Reset to page 1, or a stale page number returns an empty 200.
  const updateFilters = useCallback((next: MeasurementLogFilters) => {
    setFilters(next);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const changePage = useCallback((next: number) => setPage(next), []);

  return {
    groups: data.groups,
    previousOf,
    loading,
    error,
    filters,
    page,
    limit: DATES_PER_PAGE,
    totalDates: data.totalDates,
    totalPages: data.totalPages,
    totalMeasurements: data.totalMeasurements,
    refresh: fetchPage,
    updateFilters,
    clearFilters,
    changePage,
  };
};
